/**
 * SRES · Unified 9-stage Sidebar (Iter82 · Feb 2026).
 *
 * Extracted from WizardMainShell so every route in the 9-step flow
 * (/setup · /wizard/* · /logs-maps · /reports) can share the exact
 * same left rail. Consumed by `<ProjectStageLayout />`.
 *
 * The sidebar is self-contained — it fetches the project + stats via
 * TanStack Query (cache-shared with the wizard shell), resolves the
 * active stage from the URL via `resolveFlowKey`, and reads personal
 * progress actuals from localStorage.
 */
import { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, ArrowLeft, Video, LockSimple, X, ArrowRight } from "@phosphor-icons/react";
import { api, API_BASE } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { WIZARD_STEPS } from "@/pages/wizard/steps.config";


// Iter98h · Roster subcomponent — lists the "who acknowledged what"
// for the current project pulled from the new
// `GET /projects/{pid}/coach-acks/roster` endpoint.
function CoachAckRoster({ pid }) {
  const q = useQuery({
    queryKey: ["coach-acks-roster", pid],
    queryFn:  () => api.get(`/projects/${pid}/coach-acks/roster`).then((r) => r.data),
    enabled:  !!pid,
    staleTime: 15_000,
  });
  const entries = q.data?.entries || [];
  if (q.isLoading || entries.length === 0) return null;
  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-white"
             data-testid="wizard-coach-ack-roster">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
        <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">
          Acknowledgement roster
        </div>
        <span className="text-[10px] text-slate-400 font-mono">({entries.length})</span>
        {/* Iter98i · One-click CSV export for compliance / auditor hand-off. */}
        <a
          href={`${API_BASE}/projects/${pid}/coach-acks/roster.csv`}
          target="_blank" rel="noreferrer"
          className="ml-auto text-[10.5px] font-semibold text-brand-blue hover:text-brand-blue/80 underline"
          data-testid="wizard-coach-ack-roster-csv">
          Download CSV
        </a>
      </div>
      <ul className="max-h-32 overflow-y-auto divide-y divide-slate-100">
        {entries.slice(0, 20).map((e, i) => (
          <li key={i}
                data-testid={`wizard-coach-ack-row-${i}`}
                className="px-3 py-1.5 text-[11.5px] flex items-center gap-2">
            <span className="font-mono text-slate-700 flex-1 truncate">{e.user}</span>
            <span className="text-slate-500 truncate">
              ticked <b className="text-slate-800">{e.step_id}</b>
            </span>
            <span className="text-[10px] font-mono text-slate-400 shrink-0">
              {String(e.acked_at || "").slice(0, 19).replace("T", " ")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

import {
  WORKFLOW_STAGES, TOTAL_EST_MIN, formatEstMin, resolveFlowKey,
} from "@/config/simplified_flow";
import { getAllActuals, formatSeconds } from "@/lib/stageActuals";

export default function StagesSidebar() {
  const { pid } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [coachStage, setCoachStage] = useState(null);
  // Iter98f · Locally-dismissed prerequisites — the Coach modal lets
  // the CP tick off a step without navigating away. State bumps on
  // every toggle so upstream blockers recompute in the same render
  // cycle.
  const [ackVersion, setAckVersion] = useState(0);
  const ackKey = useCallback(
    (stepId) => `sres.coach.ack.${pid}.${stepId}`,
    [pid],
  );
  const isAcked = useCallback((stepId) => {
    try { return window.localStorage.getItem(ackKey(stepId)) === "1"; }
    catch { return false; }
  }, [ackKey]);
  // Iter98g · Fetch remote acks on mount so they follow the CP across
  // devices. Successful fetches mirror into localStorage so the next
  // render picks them up via `isAcked`.
  const remoteAcksQ = useQuery({
    queryKey: ["coach-acks", pid],
    queryFn:  () => api.get(`/projects/${pid}/coach-acks`).then((r) => r.data),
    enabled:  !!pid,
    staleTime: 60_000,
    retry: 0,
  });
  useEffect(() => {
    const remote = remoteAcksQ.data?.acks || {};
    let touched = 0;
    for (const stepId of Object.keys(remote)) {
      try {
        if (window.localStorage.getItem(ackKey(stepId)) !== "1") {
          window.localStorage.setItem(ackKey(stepId), "1");
          touched += 1;
        }
      } catch { /* silent */ }
    }
    if (touched) setAckVersion((v) => v + 1);
  }, [remoteAcksQ.data, pid, ackKey]);

  const setAcked = (stepId, on) => {
    try {
      if (on) window.localStorage.setItem(ackKey(stepId), "1");
      else    window.localStorage.removeItem(ackKey(stepId));
    } catch { /* silent */ }
    setAckVersion((v) => v + 1);
    // Best-effort backend sync — failures don't block UX.
    if (pid) {
      api.post(`/projects/${pid}/coach-acks`, { step_id: stepId, acked: on })
        .catch(() => { /* silent */ });
    }
    // If the coach modal is open, live-refresh its blocker list.
    setCoachStage((prev) => {
      if (!prev) return prev;
      const blockers = (prev.blockers || []).filter((b) => b.stepId !== stepId || !on);
      const doneSteps = (prev.totalSteps || 0) - blockers.length;
      return { ...prev, blockers, doneSteps };
    });
  };

  const projectQ = useQuery({
    queryKey: ["project", pid],
    queryFn:  () => api.get(`/projects/${pid}`).then((r) => r.data),
    enabled:  !!pid,
    staleTime: 60_000,
  });
  const statsQ = useQuery({
    queryKey: ["project-stats", pid],
    queryFn:  () => api.get(`/projects/${pid}/stats`).then((r) => r.data),
    enabled:  !!pid,
    staleTime: 30_000,
  });
  const project = useMemo(() => projectQ.data || {}, [projectQ.data]);
  const stats = useMemo(() => statsQ.data || {}, [statsQ.data]);

  // ── Sub-step completeness (used to render the "N/N" progress and
  // the ✓ tick per stage). We call each WIZARD_STEPS.check(project, stats)
  // and roll them up per WORKFLOW_STAGES entry. Cheap — check
  // functions are pure and return `{complete, blocking, ...}`.
  const checks = useMemo(
    () => WIZARD_STEPS.map((s) => s.check(project, stats)),
    [project, stats],
  );

  // ── Personal-progress actuals — poll localStorage every 5 s so the
  // "You: X min ✓" chip stays live even on non-wizard pages. We keep
  // the actuals in state (rather than reading in a useMemo keyed on a
  // tick counter) so the lint rule doesn't flag the tick as
  // "unnecessary" — the tick's whole purpose IS to force a re-read.
  const [actuals, setActuals] = useState(() => getAllActuals(pid));
  useEffect(() => {
    // Refresh immediately when the project changes.
    setActuals(getAllActuals(pid));
    const iv = setInterval(() => setActuals(getAllActuals(pid)), 5000);
    return () => clearInterval(iv);
  }, [pid]);

  const activeStageKey = resolveFlowKey(pathname);

  return (
    <aside className="w-72 shrink-0 h-full bg-white border-r border-slate-200
                        overflow-y-auto flex flex-col" data-testid="wizard-nav">
      {/* Header ── project card */}
      <div className="px-4 py-3 border-b border-slate-200">
        <button
          onClick={() => navigate("/app/projects")}
          className="text-[10px] text-slate-500 hover:text-slate-900 font-mono
                        uppercase tracking-widest flex items-center gap-1"
          data-testid="wizard-back-to-projects">
          <ArrowLeft size={11} /> All projects
        </button>
        <div className="mt-1 text-[15px] font-bold text-slate-900 truncate"
                data-testid="wizard-project-name">
          {project.name || "…"}
        </div>
        <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
          {project.commodity && (
            <Badge className="bg-brand-blue/10 text-brand-blue border-brand-blue/30
                                 text-[9px] px-1.5 py-0">
              {project.commodity}
            </Badge>
          )}
          {project.country && <span>· {project.country}</span>}
        </div>
        <button
          onClick={() => navigate(`/app/projects/${pid}/cockpit`)}
          className="mt-3 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded border border-brand-blue/30 text-brand-blue text-[11px] font-semibold hover:bg-brand-blue/5 transition"
          data-testid="wizard-open-cockpit">
          <Video size={12} weight="fill" /> Open 3D Cockpit
        </button>
      </div>

      {/* Stage list */}
      <div className="flex-1 p-3 overflow-y-auto">
        <div className="text-[9px] uppercase tracking-[0.28em] text-slate-500 px-1 pb-1 flex items-center justify-between">
          <span>Workflow · {WORKFLOW_STAGES.length} steps</span>
          <span className="text-slate-400 normal-case tracking-normal font-mono"
                    data-testid="wizard-total-est"
                    title="Estimated end-to-end runtime">
            ≈ {TOTAL_EST_MIN} min total
          </span>
        </div>
        <div className="space-y-1" data-testid="wizard-stage-list">
          {WORKFLOW_STAGES.map((stage, stageIdx) => {
            const stepsInStage = WIZARD_STEPS
              .map((s, i) => ({ ...s, idx: i }))
              .filter((s) => (stage.wizardStepIds || []).includes(s.id));
            const totalInStage = stepsInStage.length;
            const doneInStage  = stepsInStage.filter((s) => checks[s.idx]?.complete).length;
            const activeStage  = activeStageKey === stage.key;
            const stageDone    = totalInStage > 0 && doneInStage === totalInStage;
            const a = actuals[stage.key];

            // Iter98c · Sidebar Deep-Link Guard.  Compute which stages
            // still have unmet blocking upstream prerequisites so we can
            // surface a lock icon + tooltip. Navigation is NOT blocked
            // (the user can still click) — the lock is informational,
            // showing what needs to be done first for a smooth flow.
            const upstreamBlockers = [];
            let upstreamTotalSteps = 0;
            let upstreamDoneSteps  = 0;
            for (let i = 0; i < stageIdx; i++) {
              const upStage = WORKFLOW_STAGES[i];
              const upSteps = WIZARD_STEPS
                .map((s, si) => ({ ...s, idx: si }))
                .filter((s) => (upStage.wizardStepIds || []).includes(s.id));
              for (const us of upSteps) {
                upstreamTotalSteps += 1;
                const c = checks[us.idx];
                const acked = isAcked(us.id);
                if (c?.complete || acked) upstreamDoneSteps += 1;
                if (c?.blocking && !acked) {
                  upstreamBlockers.push({
                    stageKey:   upStage.key,
                    stageLabel: upStage.label,
                    stageN:     upStage.n,
                    stepId:     us.id,
                    stepTitle:  us.title,
                    message:    c.message || "requires completion",
                    url:        upStage.url(pid),
                    acked,
                  });
                }
              }
            }
            const locked = !stageDone && !activeStage && upstreamBlockers.length > 0;
            const stageKey = stage.key;

            return (
              <button
                key={stage.key}
                type="button"
                onClick={(e) => {
                  // Iter98d · Locked-Stage Coach — clicking a locked
                  // stage opens the coach modal instead of navigating
                  // straight in; user can still click "Go anyway" to
                  // proceed. Alt/Cmd click bypasses the coach.
                  if (locked && !e.altKey && !e.metaKey) {
                    e.preventDefault();
                    setCoachStage({
                      ...stage,
                      blockers: upstreamBlockers,
                      totalSteps: upstreamTotalSteps,
                      doneSteps:  upstreamDoneSteps,
                    });
                    return;
                  }
                  navigate(stage.url(pid));
                }}
                data-testid={`wizard-stage-${stage.key}`}
                data-locked={locked ? "true" : "false"}
                aria-current={activeStage ? "step" : undefined}
                className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-md border transition-colors ${
                  activeStage
                    ? "border-brand-blue bg-brand-blue/10 text-brand-blue"
                    : locked
                    ? "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  activeStage
                    ? "bg-brand-blue text-white"
                    : (stageDone ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-600")
                }`}>
                  {stageDone && !activeStage
                    ? <CheckCircle size={11} weight="fill" />
                    : stage.n}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[12px] font-semibold truncate flex items-center gap-1">
                    {stage.label}
                    {locked && (
                      <LockSimple size={10} weight="fill"
                                    className="text-slate-400 shrink-0"
                                    data-testid={`wizard-stage-lock-${stage.key}`} />
                    )}
                  </span>
                  <span className="block text-[10px] text-slate-500 truncate">{stage.subtitle}</span>
                </span>
                <div className="flex flex-col items-end gap-0.5">
                  {stage.estMin > 0 && (
                    <span
                      data-testid={`wizard-stage-est-${stage.key}`}
                      title={`Estimated time: ${formatEstMin(stage.estMin)}`}
                      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-mono tabular-nums ring-1 ring-inset
                                     ${activeStage
                                       ? "bg-brand-blue/15 text-brand-blue ring-brand-blue/30"
                                       : "bg-slate-100 text-slate-500 ring-slate-200"}`}>
                      {formatEstMin(stage.estMin)}
                    </span>
                  )}
                  {a && a.seconds > 0 && (
                    <span
                      data-testid={`wizard-stage-actual-${stage.key}`}
                      title={`You've spent ${formatSeconds(a.seconds)} on this step across ${a.visits || 1} visit${(a.visits || 1) === 1 ? "" : "s"}`}
                      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-mono tabular-nums ring-1 ring-inset
                                    ${a.completed_at
                                      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                      : "bg-amber-50 text-amber-700 ring-amber-200"}`}>
                      You: {formatSeconds(a.seconds)}{a.completed_at ? " ✓" : ""}
                    </span>
                  )}
                  {totalInStage > 0 && (
                    <span className="text-[9px] text-slate-400 font-mono">
                      {doneInStage}/{totalInStage}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="shrink-0 px-4 py-2 border-t border-slate-100 text-[9px] text-slate-400 uppercase tracking-widest text-center">
        SRES · {WORKFLOW_STAGES.length}-step workflow
      </div>

      {/* Iter98d · Locked-Stage Coach modal */}
      {coachStage && (
        <div
          role="dialog" aria-modal="true"
          data-testid="wizard-stage-coach-modal"
          className="fixed inset-0 z-50 bg-slate-950/50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setCoachStage(null)}>
          <div className="w-full max-w-md rounded-lg bg-white shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
              <div className="mt-0.5 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 shrink-0">
                <LockSimple size={16} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Stage {coachStage.n} locked</div>
                <h3 className="mt-0.5 font-heading font-bold text-lg tracking-tight text-slate-900">
                  {coachStage.label}
                </h3>
                <p className="mt-0.5 text-[12px] text-slate-500">
                  Finish these prerequisite steps first for a smooth flow.
                </p>
                {/* Iter98e · Live progress bar over the upstream steps
                    so the user can watch the lock lift as each
                    prerequisite is ticked off. Query-driven state so
                    it updates on every render as `checks[i].complete`
                    flips. */}
                {(coachStage.totalSteps || 0) > 0 && (
                  <div className="mt-2" data-testid="wizard-stage-coach-progress">
                    <div className="flex items-center justify-between text-[10.5px] text-slate-500 mb-0.5">
                      <span>Prerequisite progress</span>
                      <span className="font-mono">
                        <b className="text-slate-800">{coachStage.doneSteps}</b> / {coachStage.totalSteps} steps · {coachStage.blockers.length} blocker{coachStage.blockers.length === 1 ? "" : "s"} left
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden ring-1 ring-slate-200">
                      <div
                        className="h-full bg-gradient-to-r from-brand-blue to-emerald-500 transition-all duration-500"
                        style={{ width: `${Math.round(100 * coachStage.doneSteps / Math.max(1, coachStage.totalSteps))}%` }}
                        data-testid="wizard-stage-coach-progress-bar" />
                    </div>
                  </div>
                )}
              </div>
              <button type="button" onClick={() => setCoachStage(null)}
                        className="text-slate-400 hover:text-slate-700"
                        data-testid="wizard-stage-coach-close">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 max-h-[50vh] overflow-y-auto">
              <ol className="space-y-2">
                {(coachStage.blockers || []).map((b, i) => (
                  <li key={`${b.stageKey}-${b.stepId}-${i}`}
                        className="rounded-md border border-slate-200 bg-slate-50 p-2.5 flex items-start gap-2"
                        data-testid={`wizard-stage-coach-blocker-${i}`}>
                    {/* Iter98f · Mark-Done shortcut. Ticking the box
                        locally acknowledges this prerequisite (kept in
                        localStorage) so the sidebar unlocks the stage
                        without a full navigation. Untick to restore. */}
                    <input
                      type="checkbox"
                      className="mt-1.5 w-3.5 h-3.5 accent-emerald-500 shrink-0"
                      title="Mark this step complete"
                      onChange={(e) => setAcked(b.stepId, e.target.checked)}
                      data-testid={`wizard-stage-coach-ack-${i}`}
                    />
                    <div className="w-6 h-6 rounded-full bg-white border border-slate-300 flex items-center justify-center text-[11px] font-bold text-slate-600 shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        Stage {b.stageN} · {b.stageLabel}
                      </div>
                      <div className="text-[13px] font-semibold text-slate-800 truncate">
                        {b.stepTitle}
                      </div>
                      <div className="text-[11.5px] text-slate-600 mt-0.5">
                        {b.message}
                      </div>
                    </div>
                    <Button size="sm" variant="outline"
                              className="h-7 text-[11px] shrink-0"
                              onClick={() => {
                                setCoachStage(null);
                                navigate(b.url);
                              }}
                              data-testid={`wizard-stage-coach-jump-${i}`}>
                      Go <ArrowRight size={10} className="ml-1" />
                    </Button>
                  </li>
                ))}
              </ol>

              {/* Iter98h · Roster — who has acknowledged what.
                  Compact table below the blockers so admins can audit
                  the sign-off trail without navigating away. */}
              <CoachAckRoster pid={pid} />
            </div>
            <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-2 bg-slate-50">
              <button
                type="button"
                onClick={() => setCoachStage(null)}
                className="text-[12px] text-slate-500 hover:text-slate-800"
                data-testid="wizard-stage-coach-cancel">
                Cancel
              </button>
              <Button size="sm"
                        onClick={() => {
                          const url = coachStage.url(pid);
                          setCoachStage(null);
                          navigate(url);
                        }}
                        className="h-8 text-[12px] bg-slate-800 hover:bg-slate-900"
                        data-testid="wizard-stage-coach-continue">
                Go anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
